import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Clock,
  Map,
  Check,
  Globe,
  Copy,
  ExternalLink,
  Info,
  ShieldCheck,
  Send,
} from 'lucide-react';
import { buildWaUrl, formatPhoneTel, formatWaNumber, parseGoogleMapsEmbedUrl } from '../../lib/contactUtils.ts';

export default function CentralContactManager() {
  const { settings, updateSettingsOnServer, showToast, copyPageUrl, navigate } = useApp();

  const [form, setForm] = useState({
    whatsappNumber: settings?.contact?.whatsappNumber || '6281234567890',
    phone: settings?.contact?.phone || '+62 812-3456-7890',
    email: settings?.contact?.email || 'halo@nusantara-creative.example.com',
    address:
      settings?.contact?.address ||
      'Jl. Raya Tangkuban Parahu No. 88, Lembang, Bandung Barat, Jawa Barat 40391',
    mapEmbedUrl:
      settings?.staticPages?.contactUs?.mapEmbedUrl ||
      'https://maps.google.com/maps?q=Lembang,Bandung&t=&z=13&ie=UTF8&iwloc=&output=embed',
    defaultWaMessage:
      settings?.contactWidget?.defaultWaMessage ||
      'Halo Admin, saya tertarik dengan layanan Anda.',
    whatsappLabel: settings?.contactWidget?.whatsappLabel || 'Chat WhatsApp CS',
    phoneLabel: settings?.contactWidget?.phoneLabel || 'Telepon Langsung',
    contactBadge: settings?.staticPages?.contactUs?.badge || 'Konsultasi & Reservasi 24/7',
    contactTitle: settings?.staticPages?.contactUs?.title || 'Hubungi Kami',
    contactSubtitle:
      settings?.staticPages?.contactUs?.subtitle ||
      'Konsultasikan kebutuhan acara, kemitraan, atau pemesanan langsung dengan tim responsif kami',
    operatingHours: 'Senin - Minggu: 08.00 - 21.00 WIB',
    widgetEnabled: settings?.contactWidget?.enabled ?? true,
    statusBadgeText: settings?.contactWidget?.statusBadgeText || '🟢 Online | Fast Response',
    popupGreeting: settings?.contactWidget?.popupGreeting || 'Butuh proposal cepat atau info paket? Hubungi kami langsung!',
  });

  const [isSaving, setIsSaving] = useState(false);

  // Sync with settings if updated externally
  useEffect(() => {
    if (settings) {
      setForm({
        whatsappNumber: settings.contact?.whatsappNumber || '6281234567890',
        phone: settings.contact?.phone || '+62 812-3456-7890',
        email: settings.contact?.email || 'halo@nusantara-creative.example.com',
        address:
          settings.contact?.address ||
          'Jl. Raya Tangkuban Parahu No. 88, Lembang, Bandung Barat, Jawa Barat 40391',
        mapEmbedUrl:
          settings.staticPages?.contactUs?.mapEmbedUrl ||
          'https://maps.google.com/maps?q=Lembang,Bandung&t=&z=13&ie=UTF8&iwloc=&output=embed',
        defaultWaMessage:
          settings.contactWidget?.defaultWaMessage ||
          'Halo Admin, saya tertarik dengan layanan Anda.',
        whatsappLabel: settings.contactWidget?.whatsappLabel || 'Chat WhatsApp CS',
        phoneLabel: settings.contactWidget?.phoneLabel || 'Telepon Langsung',
        contactBadge: settings.staticPages?.contactUs?.badge || 'Konsultasi & Reservasi 24/7',
        contactTitle: settings.staticPages?.contactUs?.title || 'Hubungi Kami',
        contactSubtitle:
          settings.staticPages?.contactUs?.subtitle ||
          'Konsultasikan kebutuhan acara, kemitraan, atau pemesanan langsung dengan tim responsif kami',
        operatingHours: 'Senin - Minggu: 08.00 - 21.00 WIB',
        widgetEnabled: settings.contactWidget?.enabled ?? true,
        statusBadgeText: settings.contactWidget?.statusBadgeText || '🟢 Online | Fast Response',
        popupGreeting: settings.contactWidget?.popupGreeting || 'Butuh proposal cepat atau info paket? Hubungi kami langsung!',
      });
    }
  }, [settings]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const cleanedWa = formatWaNumber(form.whatsappNumber);
      const cleanedPhone = form.phone.trim();
      const cleanedEmail = form.email.trim();
      const cleanedAddress = form.address.trim();
      const cleanedMapUrl = parseGoogleMapsEmbedUrl(form.mapEmbedUrl, cleanedAddress);

      // Update state with normalized URL
      setForm((prev) => ({ ...prev, mapEmbedUrl: cleanedMapUrl }));

      // 1. Update contact object
      const updatedContact = {
        whatsappNumber: cleanedWa,
        phone: cleanedPhone,
        email: cleanedEmail,
        address: cleanedAddress,
        instagram: settings?.contact?.instagram || '',
        youtube: settings?.contact?.youtube || '',
        facebook: settings?.contact?.facebook || '',
        tiktok: settings?.contact?.tiktok || '',
        linkedin: settings?.contact?.linkedin || '',
        twitter: settings?.contact?.twitter || '',
      };

      // 2. Update contactWidget object
      const updatedWidget = {
        ...(settings?.contactWidget || { enabled: true }),
        enabled: form.widgetEnabled,
        whatsappNumber: cleanedWa,
        phoneNumber: cleanedPhone,
        whatsappLabel: form.whatsappLabel,
        phoneLabel: form.phoneLabel,
        defaultWaMessage: form.defaultWaMessage,
        statusBadgeText: form.statusBadgeText,
        popupGreeting: form.popupGreeting,
      };

      // 3. Update staticPages.contactUs
      const updatedContactPage = {
        ...(settings?.staticPages?.contactUs || {}),
        title: form.contactTitle,
        subtitle: form.contactSubtitle,
        badge: form.contactBadge,
        mapEmbedUrl: cleanedMapUrl,
        content: `### Layanan Pelanggan & Konsultasi 24/7\n\nPunya pertanyaan seputar reservasi paket, pemesanan produk, atau kolaborasi? Tim kami siap merespons pesan Anda dalam waktu singkat.\n\n- **Alamat Kantor:** ${cleanedAddress}\n- **WhatsApp Fast Response:** +${cleanedWa}\n- **Telepon Langsung:** ${cleanedPhone}\n- **Email Resmi:** ${cleanedEmail}\n- **Jam Operasional:** ${form.operatingHours}`,
      };

      const updatedStaticPages = {
        ...(settings?.staticPages || {}),
        contactUs: updatedContactPage,
      };

      // 4. Update SoftSelling CTA phone text clean reference
      const updatedSoftSelling = {
        ...(settings?.softsellingCta || {}),
        phoneBtnText: 'Telepon Kantor Langsung',
      };

      const ok = await updateSettingsOnServer({
        contact: updatedContact,
        contactWidget: updatedWidget,
        staticPages: updatedStaticPages as any,
        softsellingCta: updatedSoftSelling,
      });

      if (ok) {
        showToast(
          'Semua nomor kontak, email, alamat & lokasi Google Maps berhasil disinkronkan ke seluruh bagian website!',
          'success',
          'Berhasil Disimpan'
        );
      }
    } catch (err) {
      console.error(err);
      showToast('Gagal menyimpan pusat informasi kontak.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const sampleWaUrl = buildWaUrl(form.whatsappNumber, form.defaultWaMessage);
  const sampleTelUrl = formatPhoneTel(form.phone);

  return (
    <div className="space-y-6">
      {/* Top Welcome & Quick Links */}
      <div className="bg-gradient-to-r from-emerald-900 via-teal-900 to-slate-900 p-6 sm:p-8 rounded-3xl text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold border border-emerald-400/30">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Pusat Pengaturan Terpadu (Single Source of Truth)</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            Pusat Nomor Kontak, WhatsApp & Peta Maps
          </h2>
          <p className="text-xs sm:text-sm text-emerald-100/90 max-w-2xl leading-relaxed">
            Satu tempat pengisian untuk mengatur seluruh nomor telepon, WhatsApp, email, alamat kantor, dan link embed Google Maps. Perubahan di halaman ini akan <strong>otomatis terupdate 100% sinkron</strong> ke TopBar, Header, Halaman Hubungi Kami, Footer, dan Widget Kontak Melayang.
          </p>

          <div className="pt-2 flex flex-wrap items-center gap-3 text-xs">
            <button
              onClick={() => navigate('contact')}
              className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 rounded-xl font-bold flex items-center gap-2 shadow-md transition-all active:scale-95"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Lihat Halaman Kontak Live (/contact)</span>
            </button>
            <button
              onClick={() => copyPageUrl('/contact', 'Hubungi Kami')}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold flex items-center gap-2 border border-white/20 transition-all"
            >
              <Copy className="w-4 h-4" />
              <span>Salin Link Halaman Kontak</span>
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSave} className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Form Column */}
        <div className="lg:col-span-7 space-y-6">
          {/* Main Contact Numbers Card */}
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Phone className="w-5 h-5 text-emerald-600" />
                <span>Nomor Kontak Utama & WhatsApp</span>
              </h3>
              <span className="text-[10px] font-bold text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                Otomatis Sinkron
              </span>
            </div>

            {/* WhatsApp Number */}
            <div>
              <label className="text-xs font-bold text-slate-900 block mb-1 flex items-center justify-between">
                <span>💬 Nomor WhatsApp CS Fast Response *</span>
                <span className="text-[10px] text-slate-400">Format HP/WA</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-emerald-600">
                  <MessageCircle className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  required
                  value={form.whatsappNumber}
                  onChange={(e) => setForm({ ...form, whatsappNumber: e.target.value })}
                  placeholder="Contoh: 081234567890 atau 6281234567890"
                  className="w-full pl-10 pr-4 py-3 text-xs bg-slate-50 border border-slate-300 rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:bg-white"
                />
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Diformat otomatis menjadi <code>https://wa.me/{formatWaNumber(form.whatsappNumber)}</code> untuk obrolan WhatsApp 1-klik.
              </p>
            </div>

            {/* Direct Phone Number */}
            <div>
              <label className="text-xs font-bold text-slate-900 block mb-1 flex items-center justify-between">
                <span>📞 Nomor Telepon Kantor / HP Resmi *</span>
                <span className="text-[10px] text-slate-400">Telepon Langsung</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-amber-600">
                  <Phone className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Contoh: +62 812-3456-7890 atau (022) 123-4567"
                  className="w-full pl-10 pr-4 py-3 text-xs bg-slate-50 border border-slate-300 rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:bg-white"
                />
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Ditampilkan di Topbar atas, Header, Tombol Telepon Melayang, dan Footer.
              </p>
            </div>

            {/* Email Address */}
            <div>
              <label className="text-xs font-bold text-slate-900 block mb-1">
                ✉️ Alamat Email Resmi Perusahaan *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-sky-600">
                  <Mail className="w-4 h-4" />
                </div>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="info@perusahaan.com"
                  className="w-full pl-10 pr-4 py-3 text-xs bg-slate-50 border border-slate-300 rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:bg-white"
                />
              </div>
            </div>

            {/* Default WA Message */}
            <div>
              <label className="text-xs font-bold text-slate-900 block mb-1">
                💬 Pesan Default Kirim ke WhatsApp
              </label>
              <input
                type="text"
                value={form.defaultWaMessage}
                onChange={(e) => setForm({ ...form, defaultWaMessage: e.target.value })}
                placeholder="Halo Admin, saya tertarik dengan layanan Anda."
                className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl text-slate-800 font-medium focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          {/* Floating WhatsApp & Phone Contact Buttons Card */}
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-emerald-600" />
                  <span>Tombol Chat WhatsApp & Hotline Melayang di Layar</span>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Tombol pintas di pojok kanan bawah agar pengunjung mudah menghubungi CS langsung
                </p>
              </div>

              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.widgetEnabled}
                  onChange={(e) => setForm({ ...form, widgetEnabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-900 block mb-1">
                  Label Tombol WhatsApp Melayang
                </label>
                <input
                  type="text"
                  value={form.whatsappLabel}
                  onChange={(e) => setForm({ ...form, whatsappLabel: e.target.value })}
                  placeholder="Chat WhatsApp CS"
                  className="w-full px-3.5 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-medium text-slate-800 focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-900 block mb-1">
                  Label Tombol Telepon Melayang
                </label>
                <input
                  type="text"
                  value={form.phoneLabel}
                  onChange={(e) => setForm({ ...form, phoneLabel: e.target.value })}
                  placeholder="Telepon Langsung"
                  className="w-full px-3.5 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-medium text-slate-800 focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-900 block mb-1">
                  Teks Status Online Badge
                </label>
                <input
                  type="text"
                  value={form.statusBadgeText}
                  onChange={(e) => setForm({ ...form, statusBadgeText: e.target.value })}
                  placeholder="🟢 Online | Fast Response"
                  className="w-full px-3.5 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-medium text-slate-800 focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-900 block mb-1">
                  Pesan Sapaan Popup (Greeting)
                </label>
                <input
                  type="text"
                  value={form.popupGreeting}
                  onChange={(e) => setForm({ ...form, popupGreeting: e.target.value })}
                  placeholder="Butuh proposal cepat atau info paket? Hubungi kami langsung!"
                  className="w-full px-3.5 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-medium text-slate-800 focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          {/* Location & Map Embed Card */}
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-red-500" />
                <span>Alamat Kantor & Peta Google Maps</span>
              </h3>
            </div>

            {/* Physical Address */}
            <div>
              <label className="text-xs font-bold text-slate-900 block mb-1">
                🏢 Alamat Lengkap Kantor & Cabang
              </label>
              <textarea
                rows={3}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Jl. Raya Tangkuban Parahu No. 88, Lembang, Bandung Barat..."
                className="w-full p-3 text-xs bg-slate-50 border border-slate-300 rounded-xl font-medium text-slate-800 focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            {/* Google Maps Embed Link */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-900 block flex items-center justify-between">
                <span>🗺️ Link / Kode Embed Google Maps *</span>
                <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded font-bold">
                  Dukungan Semua Format
                </span>
              </label>
              <input
                type="text"
                value={form.mapEmbedUrl}
                onChange={(e) => setForm({ ...form, mapEmbedUrl: e.target.value })}
                placeholder="Tempel link Google Maps, kode iframe <iframe src=...>, link tempat, atau alamat kantor"
                className="w-full px-3.5 py-2.5 text-xs font-mono bg-slate-50 border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500"
              />
              <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                <p className="text-[11px] text-slate-500">
                  Dapat berupa link biasa, kode embed HTML <code>&lt;iframe src=&quot;...&quot;&gt;</code>, link share, atau alamat kantor.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (!form.address.trim()) {
                      showToast('Silakan isi Alamat Kantor di atas terlebih dahulu.', 'error');
                      return;
                    }
                    const generated = parseGoogleMapsEmbedUrl(form.address);
                    setForm((prev) => ({ ...prev, mapEmbedUrl: generated }));
                    showToast('Peta otomatis dikonversi berdasarkan alamat kantor!', 'success');
                  }}
                  className="px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-900 text-[11px] font-bold rounded-xl border border-emerald-300 transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  <MapPin className="w-3.5 h-3.5 text-emerald-700" />
                  <span>⚡ Buat Peta Otomatis Dari Alamat Kantor</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Preview Column */}
        <div className="lg:col-span-5 space-y-6">
          {/* Action Button Card */}
          <div className="bg-emerald-950 p-6 rounded-3xl border border-emerald-800 text-white space-y-4 shadow-lg">
            <div className="flex items-center gap-2 text-emerald-400 font-bold text-xs">
              <ShieldCheck className="w-4 h-4" />
              <span>Simpan & Terapkan Ke Seluruh Website</span>
            </div>
            <p className="text-xs text-emerald-200/90 leading-relaxed">
              Tekan tombol di bawah untuk mengaplikasikan nomor WhatsApp <strong>+{formatWaNumber(form.whatsappNumber)}</strong>, Telepon <strong>{form.phone}</strong>, dan Peta Maps secara langsung.
            </p>

            <button
              type="submit"
              disabled={isSaving}
              className="w-full py-4 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-sm rounded-2xl shadow-xl flex items-center justify-center gap-2 transition-all active:scale-98 cursor-pointer"
            >
              <Check className="w-5 h-5" />
              <span>{isSaving ? 'Menyimpan...' : 'Simpan Semua Data Kontak & Peta'}</span>
            </button>
          </div>

          {/* Live Google Maps Preview Box */}
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-xs space-y-3">
            <h4 className="font-bold text-sm text-slate-900 pb-2 border-b border-slate-100 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Map className="w-4 h-4 text-emerald-600" />
                <span>Pratinjau Peta Google Maps</span>
              </span>
              <span className="text-[10px] text-emerald-700 bg-emerald-50 font-bold px-2 py-0.5 rounded border border-emerald-200">
                Peta Live
              </span>
            </h4>

            {form.mapEmbedUrl || form.address ? (
              <div className="rounded-2xl overflow-hidden border border-slate-200 h-56 bg-slate-100 shadow-inner">
                <iframe
                  src={parseGoogleMapsEmbedUrl(form.mapEmbedUrl, form.address)}
                  title="Pratinjau Peta Kantor"
                  className="w-full h-full border-0"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className="h-44 bg-slate-50 rounded-2xl flex items-center justify-center text-xs text-slate-400 border border-dashed border-slate-300">
                Masukkan URL Google Maps untuk melihat pratinjau
              </div>
            )}
          </div>

          {/* Interactive Test Links */}
          <div className="bg-slate-50 p-5 rounded-3xl border border-slate-200 space-y-3 text-xs">
            <h4 className="font-bold text-slate-900 flex items-center gap-2">
              <Info className="w-4 h-4 text-sky-600" />
              <span>Uji Coba Tautan Kontak Langsung</span>
            </h4>

            <div className="space-y-2">
              <a
                href={sampleWaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full px-3.5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl flex items-center justify-between text-xs transition-colors"
              >
                <span className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  <span>Test Link WA (+{formatWaNumber(form.whatsappNumber)})</span>
                </span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>

              <a
                href={`tel:${sampleTelUrl}`}
                className="w-full px-3.5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl flex items-center justify-between text-xs transition-colors"
              >
                <span className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-amber-400" />
                  <span>Test Link Telepon ({form.phone})</span>
                </span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
